import { z } from 'zod';

export const contactTopics = ['sales', 'enterprise', 'support', 'other'] as const;

export type ContactTopic = (typeof contactTopics)[number];

export const contactInquirySchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120),
  email: z.string().trim().email('Enter a valid email address').max(254),
  company: z.string().trim().max(200).optional(),
  topic: z.enum(contactTopics),
  message: z.string().trim().min(10, 'Message must be at least 10 characters').max(5000),
  /** Honeypot — must be empty for real submissions. */
  website: z.string().optional().default(''),
  pageUrl: z.string().max(2000).optional(),
});

export type ContactInquiryInput = z.infer<typeof contactInquirySchema>;

export function contactTopicLabel(topic: ContactTopic): string {
  switch (topic) {
    case 'sales':
      return 'Sales';
    case 'enterprise':
      return 'Enterprise';
    case 'support':
      return 'Support';
    case 'other':
      return 'Other';
  }
}

export function parseContactTopicParam(value: string | null | undefined): ContactTopic {
  if (value === 'sales' || value === 'enterprise' || value === 'support' || value === 'other') {
    return value;
  }

  return 'other';
}

export function resolveContactInboxFromEnv(
  topic: ContactTopic,
  env: {
    CONTACT_SALES_INBOX_EMAIL: string;
    CONTACT_SUPPORT_INBOX_EMAIL: string;
  }
): string {
  if (topic === 'support') {
    return env.CONTACT_SUPPORT_INBOX_EMAIL;
  }

  return env.CONTACT_SALES_INBOX_EMAIL;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildContactInquiryHtml(input: ContactInquiryInput): string {
  const rows = [
    ['Topic', contactTopicLabel(input.topic)],
    ['Name', input.name],
    ['Email', input.email],
    ...(input.company ? [['Company', input.company] as const] : []),
    ...(input.pageUrl ? [['Submitted from', input.pageUrl] as const] : []),
  ];

  const tableRows = rows
    .map(
      ([label, value]) =>
        `<tr><td style="padding:8px 12px;font-weight:600;vertical-align:top;">${escapeHtml(label)}</td><td style="padding:8px 12px;">${escapeHtml(value)}</td></tr>`
    )
    .join('');

  return `
    <div style="font-family:system-ui,sans-serif;color:#1a1a1a;">
      <h2 style="margin:0 0 16px;">New Wisprs contact inquiry</h2>
      <table style="border-collapse:collapse;width:100%;max-width:560px;">${tableRows}</table>
      <h3 style="margin:24px 0 8px;">Message</h3>
      <p style="white-space:pre-wrap;margin:0;line-height:1.5;">${escapeHtml(input.message)}</p>
    </div>
  `.trim();
}
